import React, { useState } from 'react';
import { X, Star, Heart, Sparkles } from 'lucide-react';
import { usePaymentModal } from '../hooks/usePaymentModal';
import { useLanguage } from '../contexts/LanguageContext';

const PaymentModal = ({ isOpen, onClose, onSuccess, targetPlan = null }) => {
  const { isLoading, error, processPayment } = usePaymentModal();
  const { tr } = useLanguage();
  const [loadingPackage, setLoadingPackage] = useState(null);

  if (!isOpen) return null;

  const handlePayment = async (packageId) => {
    try {
      setLoadingPackage(packageId);
      await processPayment(packageId);
      // If successful, the user will be redirected to Stripe
      // On return, the PaymentCallback component will handle the result
    } catch (error) {
      // Error is already handled by the hook
      console.error('Payment initiation error:', error);
      setLoadingPackage(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
          disabled={loadingPackage !== null}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white p-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-2" />
          <h2 className="text-xl font-bold mb-1">{tr('Keep the magic alive!', 'Gardez la magie vivante !')}</h2>
          <p className="text-purple-100 text-sm">
            {tr('Continue creating wonderful bedtime moments that spark imagination and bring sweet dreams', "Continuez à créer de merveilleux moments du soir qui éveillent l'imagination et apportent de doux rêves")}
          </p>
        </div>

        {/* Payment Options */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Buy a Story Option - Show if no targetPlan or targetPlan is on_demand */}
          {(!targetPlan || targetPlan === 'on_demand') && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-l-4 border-yellow-400">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Star className="w-6 h-6 text-yellow-500" />
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {targetPlan ? tr('Switch to On Demand', 'Passer à On Demand') : tr('Buy a story', 'Acheter une histoire')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {targetPlan ? tr('Pay per story - Unlimited', "Payez par histoire - Illimité") : tr('Perfect to try', 'Parfait pour essayer')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-800">$0.49</div>
                  <div className="text-sm text-gray-500">{tr('per story', 'par histoire')}</div>
                </div>
              </div>
              
              <button
                onClick={() => handlePayment('story')}
                disabled={loadingPackage !== null}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 px-6 rounded-xl font-bold transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingPackage === 'story' ? tr('Processing...', 'Traitement...') : (targetPlan ? tr('Switch to On Demand', 'Passer à On Demand') : tr('Buy a story', 'Acheter une histoire'))}
              </button>
            </div>
          )}

          {/* Standard Subscription Option - Show if no targetPlan or targetPlan is standard */}
          {(!targetPlan || targetPlan === 'standard') && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Heart className="w-6 h-6 text-blue-500" />
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {targetPlan ? tr('Switch to Standard Plan', 'Passer au plan Standard') : 'Standard'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {targetPlan ? tr('50 stories per month', '50 histoires par mois') : tr('Up to 50 stories/month', "Jusqu'à 50 histoires/mois")}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-800">$9.99</div>
                  <div className="text-sm text-gray-500">{tr('per month', 'par mois')}</div>
                </div>
              </div>
              
              <button
                onClick={() => handlePayment('standard')}
                disabled={loadingPackage !== null}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 px-6 rounded-xl font-bold transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingPackage === 'standard' ? tr('Processing...', 'Traitement...') : (targetPlan ? tr('Switch to Standard', 'Passer au Standard') : tr('Subscribe', "S'abonner"))}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 text-center">
          <p className="text-sm text-gray-500">
            {tr('Cancel anytime • Secure payment by Stripe', 'Annulez à tout moment • Paiement sécurisé par Stripe')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;